import React, { useState, useEffect } from 'react';
import config from '../config/api';
import { useAuth } from '../App';
import {
  PlusIcon,
  TicketIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  FireIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const TaskList = () => {
  const { user, hasPermission } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    type: '',
    assigned_to: ''
  });
  const [newComment, setNewComment] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);
  const [isPublic, setIsPublic] = useState(true);
  const [isInternal, setIsInternal] = useState(false);

  useEffect(() => {
    if (hasPermission('task', 'read')) {
      loadTasks();
      loadUsers();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [tasks, filters]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}/tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const applyFilters = () => {
    let filtered = tasks;
    
    if (filters.status) {
      filtered = filtered.filter(task => task.status === filters.status);
    }
    if (filters.priority) {
      filtered = filtered.filter(task => task.priority === filters.priority);
    }
    if (filters.type) {
      filtered = filtered.filter(task => task.type === filters.type);
    }
    if (filters.assigned_to) {
      filtered = filtered.filter(task => task.assigned_to === parseInt(filters.assigned_to));
    }
    
    setFilteredTasks(filtered);
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${config.apiUrl}/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        await loadTasks();
        if (selectedTask && selectedTask.id === taskId) {
          const updatedTask = await response.json();
          setSelectedTask(updatedTask);
        }
      }
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const assignTask = async (taskId, userId) => {
    try {
      const response = await fetch(`${config.apiUrl}/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ assigned_to: userId })
      });
      
      if (response.ok) {
        await loadTasks();
        if (selectedTask && selectedTask.id === taskId) {
          const updatedTask = await response.json();
          setSelectedTask(updatedTask);
        }
      }
    } catch (error) {
      console.error('Error assigning task:', error);
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedTask) return;
    try {
      const response = await fetch(`${config.apiUrl}/tasks/${selectedTask.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          task_id: selectedTask.id,
          comment: newComment,
          is_internal: isInternal,
          is_public: isPublic
        })
      });
      if (response.ok) {
        const comment = await response.json();
        setSelectedTask({
          ...selectedTask,
          comments: [...selectedTask.comments, comment]
        });
        setNewComment('');
        await loadTasks();
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'DRAFT': 'Bản nháp',
      'OPEN': 'Mở',
      'PROCESSING': 'Đang xử lý',
      'CLOSE': 'Đã đóng',
      'REOPEN': 'Mở lại'
    };
    return statusMap[status] || status;
  };

  const getPriorityDisplay = (priority) => {
    const priorityMap = {
      'LOW': 'Thấp',
      'MEDIUM': 'Trung bình',
      'HIGH': 'Cao',
      'URGENT': 'Khẩn cấp'
    };
    return priorityMap[priority] || priority;
  };

  const getTypeDisplay = (type) => {
    const typeMap = {
      'SUPPORT': 'Hỗ trợ',
      'BUG': 'Báo lỗi',
      'FEATURE': 'Tính năng',
      'OTHER': 'Khác'
    };
    return typeMap[type] || type;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'LOW': 'text-gray-600 bg-gray-100',
      'MEDIUM': 'text-blue-600 bg-blue-100',
      'HIGH': 'text-yellow-600 bg-yellow-100',
      'URGENT': 'text-red-600 bg-red-100'
    };
    return colors[priority] || 'text-gray-600 bg-gray-100';
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'text-gray-600 bg-gray-100',
      'OPEN': 'text-blue-600 bg-blue-100',
      'PROCESSING': 'text-yellow-600 bg-yellow-100',
      'CLOSE': 'text-green-600 bg-green-100',
      'REOPEN': 'text-orange-600 bg-orange-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'LOW':
        return <ClockIcon className="w-4 h-4" />;
      case 'MEDIUM':
        return <ExclamationCircleIcon className="w-4 h-4" />;
      case 'HIGH':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'URGENT':
        return <FireIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const getAssignedUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Chưa phân công';
  };

  if (!hasPermission('task', 'read')) {
    return (
      <div className="p-6 text-center">
        <XCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-2" />
        <p className="text-gray-500">Bạn không có quyền xem danh sách task</p>
      </div>
    );
  }

  if (selectedTask) {
    return (
      <div className="space-y-6">
        {/* Task detail view */}
        <button
          onClick={() => setSelectedTask(null)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          ← Quay lại danh sách
        </button>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                #{selectedTask.id} - {selectedTask.title}
              </h1>
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTask.status)}`}>
                  {getStatusDisplay(selectedTask.status)}
                </span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedTask.priority)}`}>
                  {getPriorityIcon(selectedTask.priority)}
                  <span className="ml-1">{getPriorityDisplay(selectedTask.priority)}</span>
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100">
                  {getTypeDisplay(selectedTask.type)}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {hasPermission('task', 'write') && (
                <>
                  <select
                    value={selectedTask.status}
                    onChange={(e) => updateTaskStatus(selectedTask.id, e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1"
                  >
                    <option value="DRAFT">Bản nháp</option>
                    <option value="OPEN">Mở</option>
                    <option value="PROCESSING">Đang xử lý</option>
                    <option value="CLOSE">Đã đóng</option>
                    <option value="REOPEN">Mở lại</option>
                  </select>
                  <select
                    value={selectedTask.assigned_to || ''}
                    onChange={(e) => assignTask(selectedTask.id, e.target.value ? parseInt(e.target.value) : null)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1"
                  >
                    <option value="">Chưa phân công</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.full_name}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <p><strong>Người tạo:</strong> {selectedTask.guest_email ? `${selectedTask.guest_name} (${selectedTask.guest_email})` : 'Khách hàng'}</p>
              <p><strong>Phân công:</strong> {getAssignedUserName(selectedTask.assigned_to)}</p>
            </div>
            <div>
              <p><strong>Tạo:</strong> {formatDate(selectedTask.created_at)}</p>
              <p><strong>Cập nhật:</strong> {formatDate(selectedTask.updated_at)}</p>
            </div>
          </div>
          
          {selectedTask.description && (
            <div className="mb-4">
              <h3 className="font-medium text-gray-900 mb-2">Mô tả:</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{selectedTask.description}</p>
            </div>
          )}

          {/* Comments section */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              <ChatBubbleLeftRightIcon className="w-5 h-5 inline mr-2" />
              Trao đổi ({selectedTask.comments?.length || 0})
            </h3>
            
            <div className="space-y-4 mb-6">
              {selectedTask.comments?.map((comment) => (
                <div key={comment.id} className={`border-l-4 pl-4 py-2 ${comment.is_internal ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium text-sm text-gray-900">
                      {comment.user_id ? getAssignedUserName(comment.user_id) : comment.guest_email}
                      {comment.is_internal && <span className="ml-2 text-xs text-yellow-600">(Nội bộ)</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate(comment.created_at)}
                    </div>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                </div>
              ))}
            </div>

            {hasPermission('task', 'write') && (
              <form onSubmit={addComment}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thêm bình luận nội bộ
                  </label>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nhập bình luận..."
                    required
                  />
                </div>
                <div className="flex items-center gap-4 mb-2">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={isPublic}
                      onChange={() => { setIsPublic(true); setIsInternal(false); }}
                    />
                    Công khai với khách hàng
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={isInternal}
                      onChange={() => { setIsPublic(false); setIsInternal(true); }}
                    />
                    Bình luận nội bộ
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={!newComment.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Gửi bình luận
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Support Tickets</h1>
          <p className="text-gray-600">Quản lý các yêu cầu hỗ trợ từ khách hàng</p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <FunnelIcon className="w-4 h-4 mr-2" />
          Bộ lọc
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Tất cả</option>
                <option value="DRAFT">Bản nháp</option>
                <option value="OPEN">Mở</option>
                <option value="PROCESSING">Đang xử lý</option>
                <option value="CLOSE">Đã đóng</option>
                <option value="REOPEN">Mở lại</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mức độ ưu tiên</label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters({...filters, priority: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Tất cả</option>
                <option value="LOW">Thấp</option>
                <option value="MEDIUM">Trung bình</option>
                <option value="HIGH">Cao</option>
                <option value="URGENT">Khẩn cấp</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Loại</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Tất cả</option>
                <option value="SUPPORT">Hỗ trợ</option>
                <option value="BUG">Báo lỗi</option>
                <option value="FEATURE">Tính năng</option>
                <option value="OTHER">Khác</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Người phụ trách</label>
              <select
                value={filters.assigned_to}
                onChange={(e) => setFilters({...filters, assigned_to: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Tất cả</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Tasks list */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            <TicketIcon className="w-5 h-5 inline mr-2" />
            Danh sách Tickets ({filteredTasks.length})
          </h2>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Đang tải...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="p-6 text-center">
            <TicketIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Không có ticket nào</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                onClick={() => setSelectedTask(task)}
                className="p-6 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-sm font-medium text-gray-900">
                        #{task.id} - {task.title}
                      </h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                        {getStatusDisplay(task.status)}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                        {getPriorityIcon(task.priority)}
                        <span className="ml-1">{getPriorityDisplay(task.priority)}</span>
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                      {task.description}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>{getTypeDisplay(task.type)}</span>
                      <span>•</span>
                      <span>Tạo: {formatDate(task.created_at)}</span>
                      <span>•</span>
                      <span>
                        <UserIcon className="w-3 h-3 inline mr-1" />
                        {getAssignedUserName(task.assigned_to)}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <div className="text-xs text-gray-500">
                      {task.comments?.length || 0} bình luận
                    </div>
                    {task.guest_email && (
                      <div className="text-xs text-blue-600 mt-1">
                        Guest: {task.guest_name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskList;
